<template>
  <div class="p-5 text-right">
    <div @click="displayCreateComponent" class="btn btn-primary shadow-md w-40 mr-2 mb-2">
      <PlusIcon class="w-4 h-4 mr-2" /> {{ $t('reservation.add') }}
    </div>
    <div @click="exportToCsv" class="btn btn-dark shadow-md w-32 mr-2 mb-2">
      <DownloadIcon class="w-4 h-4 mr-2" /> Export
    </div>
  </div>

  <FullCalendar ref="reservations" :options="calendarOptions" />

  <CreateReservation
    ref="createReservationComponent"
    :reloadCalendar="reloadCalendar"
    :key="createReservationComponentKey"
  />
  <div
    v-if="isLoading"
    class="absolute top-0 right-0 h-full w-full bg-white opacity-50 z-50"
  ></div>
  <div id="reservation-modal" class="modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <a data-dismiss="modal" href="javascript:;">
          <XIcon class="w-8 h-8 text-gray-500" />
        </a>
        <div class="modal-body p-0">
          <div class="p-5 text-left">
            <div class="text-3xl mt-5">{{ $t('reservation.name') }}</div>
            <div class="text-gray-600 mt-2">
              {{ reservation.title }}
            </div>
            <div class="text-gray-600 mt-2">
              {{ new Date(reservation.reservationFrom).toLocaleString('sk-SK') }}
              -
              {{ new Date(reservation.reservationTo).toLocaleString('sk-SK') }}
            </div>
            <div class="text-gray-600 mt-2" v-if="!isReservationLoading">
              <div class="text-gray-600 mt-2">
                {{ $t('common.customerTime') }}:
                {{ new Date(reservation.reservationUserFrom).toLocaleString('sk-SK') }}
                -
                {{ new Date(reservation.reservationUserTo).toLocaleString('sk-SK') }}
              </div>
              <div class="text-gray-600 mt-2">
                {{ $t('common.customer') }}: {{ reservation.user.fullName }}<br />
                {{ reservation.user.email }}<br />
                {{ reservation.user.phoneNumber }}
                <br />
                {{ $t('branch.name') }}: {{ reservation.data.branch.name }}
                <br />
                {{ $t('common.note') }}: {{ reservation.note }}
              </div>
              <div
                class="text-gray-600 mt-2"
                v-if="
                  reservation.strategy == 'service-branch-place-date' ||
                  reservation.strategy == 'service-branch-place-date-range'
                "
              >
                {{ $t('branchPlace.name') }}:
                {{ reservation.data.branchPlace.internalName }}
              </div>
              <div
                class="text-gray-600 mt-2"
                v-if="
                  reservation.strategy == 'service-resource-date' ||
                  reservation.strategy == 'service-resource-date-range'
                "
              >
                {{ $t('resource.name') }}:
                {{ reservation.data.resource.internalName }}
              </div>
              <div
                class="text-gray-600 mt-2"
                v-if="reservation.strategy == 'service-employee'"
              >
                {{ $t('employee.name') }}: {{ reservation.data.employee.name }}
                {{ reservation.data.employee.lastname }}
              </div>
            </div>
            <div
              class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center"
              v-if="isReservationLoading || this.isReservationDeleting"
            >
              <LoadingIcon icon="three-dots" class="w-8 h-8" />
            </div>
          </div>
          <div class="flex mb-3">
            <div class="px-5 pb-8 text-center flex-1">
              <button
                type="button"
                data-dismiss="modal"
                v-show="!this.isReservationDeleteConfirm"
                class="btn btn-outline-secondary w-40 dark:border-dark-5 dark:text-gray-300 mr-1"
              >
                {{ $t('common.closeDetail') }}
              </button>
            </div>
            <div class="px-5 pb-8 text-center flex-1">
              <button
                type="button"
                class="btn btn-danger w-40"
                v-if="this.isReservationCancelable"
                v-show="!this.isReservationDeleteConfirm"
                @click="deleteEventConfirm"
              >
                {{ $t('common.deleteReservation') }}
              </button>
              <button
                type="button"
                class="btn btn-danger w-40"
                v-if="this.isReservationDeleteConfirm && !this.isReservationDeleting"
                @click="deleteEventConfirm"
              >
                {{ $t('common.deleteConfirmReservation') }}
              </button>
            </div>
          </div>
          <div
            class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center"
            v-if="this.isReservationStateProcessing"
          >
            <LoadingIcon icon="three-dots" class="w-8 h-8" />
          </div>
          <div v-if="!isReservationStateProcessing">
            <div class="flex">
              <div
                class="px-5 pb-8 pt-4 text-left flex-1"
                v-show="reservation.state !== 'init'"
              >
                {{ $t('reservation.state.title') }}:
                {{ $t('reservation.state.' + reservation.state) }}
              </div>
            </div>
            <div class="flex">
              <div class="px-5 pb-8 text-center flex-1">
                <button
                  type="button"
                  @click="changeReservationState('DONE_SUCCESS')"
                  v-show="this.isPointConfirm === null"
                  class="btn btn-success w-40 dark:border-dark-5 dark:text-gray-300 mr-1"
                >
                  {{ $t('reservation.state.DONE_SUCCESS') }}
                </button>
              </div>
              <div class="px-5 pb-8 text-center flex-1">
                <button
                  type="button"
                  @click="changeReservationState('DONE_CUSTOMER_DID_NOT_COME')"
                  v-show="this.isPointConfirm === null"
                  class="btn btn-warning w-40 dark:border-dark-5 dark:text-gray-300 mr-1"
                >
                  {{ $t('reservation.state.DONE_CUSTOMER_DID_NOT_COME') }}
                </button>
              </div>
            </div>
          </div>
          <div
            class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center"
            v-if="this.isPointProcessing"
          >
            <LoadingIcon icon="three-dots" class="w-8 h-8" />
          </div>
          <div
            v-if="
              !isPointProcessing &&
              !isReservationLoading &&
              reservation.user.uuid !== null
            "
          >
            <div class="flex">
              <div
                class="px-5 pb-8 pt-4 text-left flex-1"
                v-show="reservation.loyalty.note !== ''"
              >
                {{ $t('customer.note') }}: {{ reservation.loyalty.note }}
              </div>
            </div>
            <div class="flex">
              <div class="px-5 pb-8 pt-4 text-left flex-1">
                {{ $t('common.pointStatus') }}: {{ reservation.loyalty.points }}
              </div>
              <div class="px-5 pb-8 text-left flex-1">
                <input
                  id="points"
                  v-model.trim="validatePoints.points.$model"
                  type="number"
                  name="points"
                  class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                  :class="{
                    'border-theme-6': validatePoints.points.$error
                  }"
                />
                <template v-if="validatePoints.points.$error">
                  <div
                    v-for="(error, index) in validatePoints.points.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
            </div>
            <div class="flex">
              <div class="px-5 pb-8 text-center flex-1">
                <button
                  type="button"
                  @click="addPointConfirm"
                  v-show="this.isPointConfirm === null"
                  class="btn btn-success-soft w-40 dark:border-dark-5 dark:text-gray-300 mr-1"
                >
                  {{ $t('common.add') }}
                </button>
                <button
                  type="button"
                  @click="addPointConfirm"
                  v-show="this.isPointConfirm === 'add'"
                  class="btn btn-success w-40 dark:border-dark-5 dark:text-gray-300 mr-1"
                >
                  {{ $t('common.addConfirmPoints') }}
                </button>
              </div>
              <div
                class="px-5 pb-8 text-center flex-1"
                v-if="reservation.loyalty.points > 0"
              >
                <button
                  type="button"
                  @click="removePointConfirm"
                  v-show="this.isPointConfirm === null"
                  class="btn btn-warning-soft w-40 dark:border-dark-5 dark:text-gray-300 mr-1"
                >
                  {{ $t('common.remove') }}
                </button>
                <button
                  type="button"
                  @click="removePointConfirm"
                  v-show="this.isPointConfirm === 'remove'"
                  class="btn btn-warning w-40 dark:border-dark-5 dark:text-gray-300 mr-1"
                >
                  {{ $t('common.removeConfirmPoints') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- BEGIN: Success Notification Content -->
  <div id="success-notification-content" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('reservation.deleted') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
        Please check your e-mail for further info!
      </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('reservation.notDeleted') }}!</div>
      <!-- <div class="text-gray-600 mt-1">{{ errorNote }}</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
  <!-- BEGIN: Success Notification Content -->
  <div id="success-notification-content-points" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('points.changed') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
        Please check your e-mail for further info!
      </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-notification-content-points" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('points.notChanged') }}!</div>
      <!-- <div class="text-gray-600 mt-1">{{ errorNote }}</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
  <!-- BEGIN: Success Notification Content -->
  <div id="success-notification-content-state" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('reservation.state.changed') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
        Please check your e-mail for further info!
      </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-notification-content-state" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('reservation.state.notChanged') }}!</div>
      <!-- <div class="text-gray-600 mt-1">{{ errorNote }}</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
</template>

<script>
import { defineComponent, reactive, toRefs, ref } from 'vue'
import '@fullcalendar/core/vdom'
import FullCalendar from '@fullcalendar/vue3'
import interactionPlugin from '@fullcalendar/interaction'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import skLocales from '@fullcalendar/core/locales/sk'
import CreateReservation from '../../components/reservations/CreateReservation.vue'
import reservationsApi from '../../services/reservations/reservations.api.js'
import accountsApi from '../../services/accounts/account.api.js'
import Toastify from 'toastify-js'
import { required, helpers, minValue, integer } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

export default defineComponent({
  components: {
    FullCalendar,
    CreateReservation
  },
  methods: {
    loaderToggle(value) {
      this.isLoading = value
    },
    changeReservationState(state) {
      this.isReservationStateProcessing = true
      reservationsApi.updateReservationState(this.reservation.uuid, state).then(
        (response) => {
          Toastify({
            node: cash('#success-notification-content-state')
              .clone()
              .removeClass('hidden')[0],
            duration: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.reservation.state = response.data.state
          this.isReservationStateProcessing = false
        },
        (error) => {
          console.log(error)
          Toastify({
            node: cash('#failed-notification-content-state')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.isReservationStateProcessing = false
        }
      )
    },
    deleteEventConfirm() {
      if (this.isReservationDeleteConfirm === false) {
        this.isReservationDeleteConfirm = true
        return
      }
      this.isReservationDeleting = true
      reservationsApi.delete(this.reservation.uuid).then(
        (response) => {
          Toastify({
            node: cash('#success-notification-content').clone().removeClass('hidden')[0],
            duration: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.hideReservationModal()
          this.isReservationDeleteConfirm = false
          this.isReservationDeleting = false
          this.reloadCalendar()
        },
        (error) => {
          console.log(error)
          Toastify({
            node: cash('#failed-notification-content').clone().removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.isReservationDeleteConfirm = false
          this.isReservationDeleting = false
        }
      )
    },
    addPointConfirm() {
      this.validatePoints.$touch()
      if (this.validatePoints.points.$error) {
        return
      }
      if (this.isPointConfirm !== 'add') {
        this.isPointConfirm = 'add'
        return
      }

      this.isPointProcessing = true

      const data = {
        userUUID: this.reservation.user.uuid,
        points: this.formData.points
      }
      accountsApi.add(data).then(
        (response) => {
          Toastify({
            node: cash('#success-notification-content-points')
              .clone()
              .removeClass('hidden')[0],
            duration: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.reservation.loyalty.points = response.data.points
          this.isPointConfirm = null
          this.isPointProcessing = false
          this.formData.points = 0
        },
        (error) => {
          console.log(error)
          Toastify({
            node: cash('#failed-notification-content-points')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.isPointConfirm = null
          this.isPointProcessing = false
        }
      )
    },
    removePointConfirm() {
      this.validatePoints.$touch()
      if (this.validatePoints.points.$error) {
        return
      }
      if (this.isPointConfirm !== 'remove') {
        this.isPointConfirm = 'remove'
        return
      }
      this.isPointProcessing = true

      const data = {
        userUUID: this.reservation.user.uuid,
        points: this.formData.points
      }
      accountsApi.remove(data).then(
        (response) => {
          Toastify({
            node: cash('#success-notification-content-points')
              .clone()
              .removeClass('hidden')[0],
            duration: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.reservation.loyalty.points = response.data.points
          this.isPointConfirm = null
          this.isPointProcessing = false
          this.formData.points = 0
        },
        (error) => {
          console.log(error)
          Toastify({
            node: cash('#failed-notification-content-points')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.isPointConfirm = null
          this.isPointProcessing = false
        }
      )
    },
    displayReservation(info) {
      this.isReservationDeleteConfirm = false
      this.isReservationLoading = true
      this.isReservationDeleting = false
      this.isPointConfirm = null
      this.isPointProcessing = false
      this.formData.points = 0
      this.reservation = info.event.extendedProps
      this.showReservationModal()
      reservationsApi.getDetail(info.event.id).then(
        (response) => {
          this.reservation = response.data
          this.isReservationCancelable =
            new Date().getTime() - Date.parse(response.data.reservationTo) <= 0
          this.isReservationLoading = false
        },
        (error) => {
          console.log(error)
          this.isReservationLoading = false
        }
      )
    },
    displayCreateComponent() {
      this.$refs.createReservationComponent.displayComponent()
    },
    reloadCalendar() {
      this.$refs.reservations.getApi().refetchEvents()
      this.createReservationComponentKey++
    },
    loadEvents(info, successCallback, failureCallback) {
      reservationsApi
        .getForCalendar(
          info.start.toISOString(),
          info.end.toISOString(),
          this.service,
          this.branch,
          this.employee,
          this.branchPlace,
          this.resource,
          this.customer
        )
        .then(
          (response) => {
            successCallback(
              response.data.data.map(function (item) {
                return {
                  id: item.uuid,
                  title: item.title,
                  start: item.reservationFrom,
                  end: item.reservationTo,
                  backgroundColor: item.color,
                  extendedProps: item
                }
              })
            )
          },
          (error) => {
            failureCallback(error)
          }
        )
    },
    exportToCsv() {
      reservationsApi
        .getCsvExport(
          this.$refs.reservations.calendar.view.currentStart.toISOString(),
          this.$refs.reservations.calendar.view.currentEnd.toISOString(),
          this.service,
          this.branch,
          this.employee,
          this.branchPlace,
          this.resource,
          this.customer
        )
        .then(
          (response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'reservation.csv')
            document.body.appendChild(link)
            link.click()
          },
          (error) => {
            console.log(error)
          }
        )
    }
  },
  data() {
    return {
      isLoading: true,
      isReservationCancelable: false,
      isReservationLoading: true,
      isReservationDeleteConfirm: false,
      isReservationDeleting: false,
      isPointConfirm: null,
      isPointProcessing: false,
      isReservationStateProcessing: false,
      calendarApi: null,
      createReservationComponentKey: 0,
      reservation: {
        uuid: '',
        title: '',
        reservationFrom: '',
        reservationTo: '',
        user: {
          uuid: null
        }
      },
      calendarOptions: {
        plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin],
        firstDay: 1,
        locale: skLocales,
        droppable: false,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        },
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false
        },
        initialView: 'timeGridWeek',
        navLinks: true,
        editable: false,
        dayMaxEvents: true,
        loading: this.loaderToggle,
        events: this.loadEvents,
        eventClick: this.displayReservation
      }
    }
  },
  props: {
    service: {
      type: String,
      default: () => null
    },
    branch: {
      type: String,
      default: () => null
    },
    employee: {
      type: String,
      default: () => null
    },
    branchPlace: {
      type: String,
      default: () => null
    },
    resource: {
      type: String,
      default: () => null
    },
    customer: {
      type: String,
      default: () => null
    }
  },
  setup(props) {
    const showReservationModal = () => {
      cash('#reservation-modal').modal('show')
    }
    const hideReservationModal = () => {
      cash('#reservation-modal').modal('hide')
    }

    const formData = reactive({
      points: 0
    })
    const rules = {
      points: {
        required: helpers.withMessage('form.validation.points', required),
        integer,
        minValue: helpers.withMessage('form.validation.points', minValue(0))
      }
    }
    const validatePoints = useVuelidate(rules, toRefs(formData))

    return {
      validatePoints,
      formData,
      showReservationModal,
      hideReservationModal
    }
  }
})
</script>
